// Single chat room (messages + composer + live WS) for a given roomId. Used by
// the clan-detail Chat tab; chat-pane keeps its own inline copy for the DM list.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { getMessages, sendMessage, toggleReaction } from "@/api/chat";
import { chatSocket } from "@/api/chat-socket";
import type { MessageRead, ReactionSummary } from "@/api/community-types";
import { useAuth } from "@/auth/auth-context";
import { Avatar } from "@/components/avatar";
import { cx } from "@/components/ui";
import { useTranslation } from "react-i18next";

const REACTION_CHOICES = ["👍", "❤️", "😂", "🎉", "🔥", "👏"];

export function ChatRoom({ roomId }: { roomId: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const myId = user?.id;
  const [messages, setMessages] = useState<MessageRead[]>([]);
  const [draft, setDraft] = useState("");
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const append = (m: MessageRead) =>
    setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));

  const setReactions = (messageId: string, reactions: ReactionSummary[]) =>
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reactions } : m)));

  useEffect(() => {
    chatSocket.connect();
    chatSocket.subscribe(roomId);
    const off = chatSocket.onMessage((m) => {
      if (m.room_id === roomId) append(m);
    });
    // Reaction frames carry viewer-agnostic counts; preserve local "mine".
    const offFrame = chatSocket.onFrame((frame) => {
      if (frame?.type !== "reaction" || frame.room_id !== roomId) return;
      const mid = frame.message_id as string;
      const incoming = (frame.reactions as { emoji: string; count: number }[]) ?? [];
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== mid) return m;
          const reactions = incoming.map((r) => ({
            emoji: r.emoji,
            count: r.count,
            mine: m.reactions?.find((x) => x.emoji === r.emoji)?.mine ?? false,
          }));
          return { ...m, reactions };
        }),
      );
    });
    let cancelled = false;
    getMessages(roomId)
      .then((rows) => {
        if (!cancelled) setMessages([...rows].reverse());
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      off();
      offFrame();
      chatSocket.unsubscribe(roomId);
      // Don't disconnect — the socket is shared (chat-pane owns its lifecycle).
    };
  }, [roomId]);

  const react = async (messageId: string, emoji: string) => {
    setPickerFor(null);
    try {
      setReactions(messageId, await toggleReaction(roomId, messageId, emoji));
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setDraft("");
    try {
      append(await sendMessage(roomId, body));
    } catch {
      setDraft(body);
    }
  };

  return (
    <div className="card flex h-[28rem] flex-col">
      <div className="flex-1 space-y-2 overflow-y-auto p-4">
        {messages.length === 0 && (
          <p className="text-sm text-ink-faint">{t("No messages yet. Say hi 👋")}</p>
        )}
        {messages.map((m) => {
          const mine = m.sender_user_id === myId;
          return (
            <div key={m.id} className={cx("group flex items-end gap-2", mine ? "justify-end" : "justify-start")}>
              {!mine && <Avatar url={m.sender_avatar_url} name={m.sender_display_name} size={24} />}
              <div className={cx("flex max-w-[75%] flex-col", mine ? "items-end" : "items-start")}>
                <div className="relative">
                  <div
                    className={cx(
                      "rounded-2xl px-3.5 py-2 text-sm",
                      mine ? "bg-brand-600 text-white" : "bg-black/5 text-ink",
                    )}
                  >
                    {!mine && (
                      <span className="mb-0.5 block text-xs font-semibold text-brand-600">
                        {m.sender_display_name}
                      </span>
                    )}
                    <span className="whitespace-pre-wrap">{m.body}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPickerFor(pickerFor === m.id ? null : m.id)}
                    className={cx(
                      "absolute top-1/2 -translate-y-1/2 rounded-full bg-white px-1 text-xs opacity-0 shadow group-hover:opacity-100",
                      mine ? "-left-7" : "-right-7",
                    )}
                    aria-label={t("Add reaction")}
                  >
                    🙂
                  </button>
                  {pickerFor === m.id && (
                    <div
                      className={cx(
                        "absolute z-10 mt-1 flex gap-1 rounded-full border border-black/10 bg-white p-1 shadow-lg",
                        mine ? "right-0" : "left-0",
                      )}
                    >
                      {REACTION_CHOICES.map((e) => (
                        <button
                          key={e}
                          type="button"
                          onClick={() => react(m.id, e)}
                          className="rounded-full px-1 text-base hover:bg-black/5"
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {m.reactions && m.reactions.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {m.reactions.map((r) => (
                      <button
                        key={r.emoji}
                        type="button"
                        onClick={() => react(m.id, r.emoji)}
                        className={cx(
                          "rounded-full border px-1.5 py-0.5 text-xs",
                          r.mine ? "border-brand-400 bg-brand-50" : "border-black/10 bg-white",
                        )}
                      >
                        {r.emoji} {r.count}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-black/5 p-3">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("Type a message…")}
          className="flex-1 rounded-xl border border-black/10 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
        <button type="submit" className="rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white">
          {t("Send")}
        </button>
      </form>
    </div>
  );
}
